import { AnyAction } from 'redux';
import { ILoadingError } from '#/types/global';
import { IMyArtistsResponse } from '#/types/API/MyArtists';
import { IMyAlbumsResponse } from '#/types/API/myAlbums';
import { IMyPlaylistsResponse } from '#/types/API/myPlaylists';
import {
  createActionLibraryAddAlbum,
  createActionLibraryAddArtist,
  createActionLibraryAddPlaylist,
  createActionLibraryErrorAlbums,
  createActionLibraryErrorArtists,
  createActionLibraryErrorPlaylists,
  createActionLibraryLoadingAlbums,
  createActionLibraryLoadingArtists,
  createActionLibraryLoadingPlaylists,
  createActionLibraryRemoveAlbum,
  createActionLibraryRemoveArtist,
  createActionLibraryRemovePlaylist,
  createActionLibrarySetAlbums,
  createActionLibrarySetArtists,
  createActionLibrarySetPlaylists,
  createActionLibrarySetSelected,
} from '../actions/library';

export type TLibrarySelected = 'artists' | 'albums' | 'playlists';

interface IState {
  artists: IMyArtistsResponse['artists']['items'] | ILoadingError;
  albums: IMyAlbumsResponse['items'] | ILoadingError;
  playlists: IMyPlaylistsResponse['items'] | ILoadingError;
  selected: TLibrarySelected;
}

const initialState: IState = {
  artists: { _loading: true, _error: false },
  albums: { _loading: true, _error: false },
  playlists: { _loading: true, _error: false },
  selected: 'artists',
};

const library = (state: IState = initialState, action: AnyAction): IState => {
  if (createActionLibrarySetAlbums.doesMatch(action)) {
    return { ...state, albums: action.payload };
  }

  if (createActionLibraryAddAlbum.doesMatch(action)) {
    if (Array.isArray(state.albums)) {
      return { ...state, albums: [...state.albums, action.payload] };
    }
    return state;
  }

  if (createActionLibraryRemoveAlbum.doesMatch(action)) {
    if (Array.isArray(state.albums)) {
      return {
        ...state,
        albums: state.albums.filter(
          (album) => album.album.id !== action.payload
        ),
      };
    }
    return state;
  }

  if (createActionLibraryLoadingAlbums.doesMatch(action)) {
    return {
      ...state,
      albums: { ...state.albums, _loading: true },
    };
  }

  if (createActionLibraryErrorAlbums.doesMatch(action)) {
    return {
      ...state,
      albums: { ...state.albums, _error: true },
    };
  }

  if (createActionLibrarySetArtists.doesMatch(action)) {
    return { ...state, artists: action.payload };
  }

  if (createActionLibraryAddArtist.doesMatch(action)) {
    if (Array.isArray(state.artists)) {
      return { ...state, artists: [...state.artists, action.payload] };
    }
    return state;
  }

  if (createActionLibraryRemoveArtist.doesMatch(action)) {
    if (Array.isArray(state.artists)) {
      return {
        ...state,
        artists: state.artists.filter((artist) => artist.id !== action.payload),
      };
    }
    return state;
  }

  if (createActionLibraryLoadingArtists.doesMatch(action)) {
    return {
      ...state,
      artists: { ...state.artists, _loading: true },
    };
  }

  if (createActionLibraryErrorArtists.doesMatch(action)) {
    return {
      ...state,
      artists: { ...state.artists, _error: true },
    };
  }

  if (createActionLibrarySetPlaylists.doesMatch(action)) {
    return { ...state, playlists: action.payload };
  }

  if (createActionLibraryAddPlaylist.doesMatch(action)) {
    if (Array.isArray(state.playlists)) {
      return { ...state, playlists: [...state.playlists, action.payload] };
    }
    return state;
  }

  if (createActionLibraryRemovePlaylist.doesMatch(action)) {
    if (Array.isArray(state.playlists)) {
      return {
        ...state,
        playlists: state.playlists.filter(
          (playlist) => playlist.id !== action.payload
        ),
      };
    }
    return state;
  }

  if (createActionLibraryLoadingPlaylists.doesMatch(action)) {
    return {
      ...state,
      playlists: { ...state.playlists, _loading: true },
    };
  }

  if (createActionLibraryErrorPlaylists.doesMatch(action)) {
    return {
      ...state,
      playlists: { ...state.playlists, _error: true },
    };
  }

  if (createActionLibrarySetSelected.doesMatch(action)) {
    return {
      ...state,
      selected: action.payload,
    };
  }

  return state;
};

export default library;
